/**
 * Display settings section — font size, section headings, light/dark mode.
 */

import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { SunIcon, MoonIcon, MonitorIcon } from '@components/icons';

export const DisplaySection = observer(function DisplaySection() {
  const fontScale = bibleState.fontScale;
  const themeMode = bibleState.themeMode;
  const fontPercent = Math.round(fontScale * 100);

  const handleFontScaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    bibleState.setFontScale(parseFloat(e.target.value));
  };

  const handleThemeChange = (mode: 'system' | 'light' | 'dark') => {
    bibleState.setThemeMode(mode);
  };

  return (
    <section className="border-t border-stone-200/50 pt-4 dark:border-stone-700/50">
      <h2 className="mb-4 text-lg font-semibold text-stone-800 dark:text-stone-200">Display</h2>

      {/* Font Size */}
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <label className="text-sm font-medium text-stone-700 dark:text-stone-300">
            Font Size
          </label>
          <span className="text-sm text-stone-500 dark:text-stone-400">{fontPercent}%</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-stone-500 dark:text-stone-400">A</span>
          <input
            type="range"
            min="0.75"
            max="1.5"
            step="0.05"
            value={fontScale}
            onChange={handleFontScaleChange}
            className="accent-primary h-2 flex-1 cursor-pointer appearance-none rounded-lg bg-stone-200 dark:bg-stone-700"
          />
          <span className="text-lg text-stone-500 dark:text-stone-400">A</span>
        </div>
      </div>

      {/* Section Headings Toggle */}
      <div className="mb-6">
        <label className="flex cursor-pointer items-center justify-between">
          <span className="text-sm font-medium text-stone-700 dark:text-stone-300">
            Section Headings
          </span>
          <button
            role="switch"
            aria-checked={bibleState.showPericopeHeadings}
            onClick={() => bibleState.setShowPericopeHeadings(!bibleState.showPericopeHeadings)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              bibleState.showPericopeHeadings ? 'bg-primary' : 'bg-stone-300 dark:bg-stone-600'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                bibleState.showPericopeHeadings ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </label>
        <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
          Show pericope titles above passage sections
        </p>
      </div>

      {/* Reading Mode Toggle */}
      <div className="mb-6">
        <label className="flex cursor-pointer items-center justify-between">
          <span className="text-sm font-medium text-stone-700 dark:text-stone-300">
            Reading Mode
          </span>
          <button
            role="switch"
            data-testid="reading-mode-toggle"
            aria-checked={bibleState.isReadingMode}
            onClick={() => bibleState.setReadingMode(!bibleState.isReadingMode)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              bibleState.isReadingMode ? 'bg-primary' : 'bg-stone-300 dark:bg-stone-600'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                bibleState.isReadingMode ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </label>
        <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
          Read without tapping into Strong&apos;s — word lookups stay off until you switch back
        </p>
      </div>

      {/* Light/Dark Mode */}
      <div>
        <label className="mb-2 block text-sm font-medium text-stone-700 dark:text-stone-300">
          Mode
        </label>
        <div className="grid grid-cols-3 gap-2">
          {(['system', 'light', 'dark'] as const).map((mode) => {
            const Icon = mode === 'system' ? MonitorIcon : mode === 'light' ? SunIcon : MoonIcon;
            const label = mode.charAt(0).toUpperCase() + mode.slice(1);
            return (
              <button
                key={mode}
                onClick={() => handleThemeChange(mode)}
                className={`flex flex-col items-center gap-2 rounded-xl border p-3 transition-all ${
                  themeMode === mode
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-stone-200 text-stone-600 hover:border-stone-300 dark:border-stone-700 dark:text-stone-400 dark:hover:border-stone-600'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
});
